import { ArrowUpRight, X } from "lucide-react"

import { BrandLogo } from "@/shared/components/BrandLogo"
import {
  SheetClose,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/shared/components/ui/sheet"
import { ToggleTheme } from "@/shared/components/ui/toggle-theme"
import { buildContact, buildNav } from "@/shared/i18n/legacyShape"
import { useLocaleCopy } from "@/shared/i18n/useLocaleCopy"
import { cn } from "@/shared/utils"

const MENU_DESCRIPTION = "Navegação principal"
const CLOSE_LABEL = "Fechar menu"
const CONTACT_EYEBROW = "Contato"

type NavbarMobileMenuProps = {
  onClose: () => void
}

function MobileMenuHeader() {
  return (
    <SheetHeader className="flex flex-row items-center justify-between gap-4 border-b border-mesa-border px-5 py-4">
      <SheetTitle asChild>
        <span className="text-mesa-primary">
          <BrandLogo size="sm" />
        </span>
      </SheetTitle>
      <SheetDescription className="sr-only">
        {MENU_DESCRIPTION}
      </SheetDescription>
      <div className="flex items-center gap-2">
        <ToggleTheme className="h-8 w-8" />
        <SheetClose asChild>
          <button
            type="button"
            aria-label={CLOSE_LABEL}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full text-mesa-text-muted transition-colors duration-200 ease-out hover:text-mesa-primary"
          >
            <X className="h-4 w-4" aria-hidden="true" strokeWidth={1.5} />
          </button>
        </SheetClose>
      </div>
    </SheetHeader>
  )
}

function MobileMenuLinks({ onClose }: NavbarMobileMenuProps) {
  const copy = useLocaleCopy()
  const NAV = buildNav(copy)
  return (
    <nav aria-label={MENU_DESCRIPTION} className="flex flex-1 flex-col px-5 py-6">
      {NAV.links.map((link, index) => (
        <a
          key={link.href}
          href={link.href}
          onClick={onClose}
          className="group flex cursor-pointer items-baseline gap-3.5 py-3"
        >
          <span className="font-mono text-[10px] font-normal text-mesa-text-muted/60 tabular-nums">
            {String(index + 1).padStart(2, "0")}
          </span>
          <span
            className={cn(
              "text-[15px] font-medium tracking-tight text-mesa-text",
              "transition-colors duration-200 ease-out group-hover:text-mesa-primary",
            )}
          >
            {link.label}
          </span>
        </a>
      ))}
    </nav>
  )
}

function MobileMenuFooter({ onClose }: NavbarMobileMenuProps) {
  const copy = useLocaleCopy()
  const NAV = buildNav(copy)
  const CONTACT = buildContact(copy)
  return (
    <div className="flex flex-col gap-5 border-t border-mesa-border px-5 py-5">
      <div className="flex flex-col gap-2">
        <span className="text-[9px] font-semibold uppercase tracking-[0.22em] text-mesa-text-muted/75">
          {CONTACT_EYEBROW}
        </span>
        <div className="flex flex-col gap-1">
          <a
            href={CONTACT.altChannels.emailLink}
            onClick={onClose}
            className="cursor-pointer text-[12px] text-mesa-text-muted transition-colors duration-200 ease-out hover:text-mesa-primary"
          >
            {CONTACT.altChannels.email}
          </a>
        </div>
      </div>

      <a
        href="#contato"
        onClick={onClose}
        className={cn(
          "group inline-flex h-11 w-full cursor-pointer items-center justify-center gap-2 rounded-full px-5",
          "bg-mesa-primary text-[12px] font-medium tracking-wide text-primary-foreground",
          "transition-colors duration-200 ease-out hover:bg-mesa-primary-alt",
        )}
      >
        {NAV.cta}
        <ArrowUpRight
          className="h-3.5 w-3.5 transition-transform duration-200 ease-out group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          aria-hidden="true"
        />
      </a>
    </div>
  )
}

export function NavbarMobileMenu({ onClose }: NavbarMobileMenuProps) {
  return (
    <SheetContent
      side="right"
      showCloseButton={false}
      className="top-2! right-2 bottom-2! flex h-auto w-[82%] max-w-xs flex-col overflow-hidden rounded-2xl border border-mesa-border bg-card p-0 text-foreground sm:max-w-xs"
    >
      <MobileMenuHeader />
      <MobileMenuLinks onClose={onClose} />
      <MobileMenuFooter onClose={onClose} />
    </SheetContent>
  )
}
